import { memo, useState, useEffect } from "react";
import { RefreshCw, Zap, PanelRightOpen } from "lucide-react";
import { getBatterIndex, getLineup, getBench } from "./utils";
import { TEAM_COLORS, DEFAULT_TEAM_COLOR } from "./constants";

function teamColor(colorKey) {
  return TEAM_COLORS[colorKey] || DEFAULT_TEAM_COLOR;
}

// Returns a compact season-range string from the player's trimmed seasons array.
// e.g. seasons spanning 1977–1983 → "1977–83", single season → "1977".
function trimRange(player) {
  const years = (player?.seasons || []).map(s => s?.year).filter(Boolean);
  if (!years.length) return player?.year ? String(player.year) : null;
  const lo = Math.min(...years);
  const hi = Math.max(...years);
  if (lo === hi) return String(lo);
  return `${lo}–${String(hi).slice(2)}`;
}

// Current Pitcher Display
function CurrentPitcherDisplay({ game, onPitcherSubstitute, onPitcherInfo, canEdit = true }) {
  const fieldingTeam = game.state.half === "top" ? game.home_team : game.away_team;
  const pitcher = fieldingTeam.players.find(p => p.position === "P" && !p.subbed_out);
  const pitchersAvailable = fieldingTeam.players.filter(p => p.position === "P" && !p.subbed_out).length;
  const [menuOpen, setMenuOpen] = useState(false);
  const tc = teamColor(fieldingTeam.color);

  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(false);
    if (menuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuOpen]);

  if (!pitcher) return null;

  return (
    <div data-testid="current-pitcher" className="sidebar-section">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-black tracking-widest ${tc.text}`}>Pitching</span>
        <span className="text-[9px] text-slate-500">{fieldingTeam.name}</span>
      </div>
      <div className="relative">
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(!menuOpen); }}
          className="w-full flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/50 hover:bg-slate-800 transition-colors text-left border border-slate-700/50"
        >
          <div className={`w-11 h-11 rounded-lg ${tc.bg} border ${tc.border} flex items-center justify-center font-mono font-black ${tc.text} text-sm`}>
            P
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm truncate text-white">{pitcher.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded font-bold">
                {pitcher.pitch_chips || 4} chips
              </span>
              {trimRange(pitcher) && (
                <span className="text-[9px] text-slate-600 font-mono">{trimRange(pitcher)}</span>
              )}
            </div>
          </div>
        </button>
        
        {menuOpen && (
          <div 
            onClick={(e) => e.stopPropagation()}
            className="absolute left-0 right-0 top-full mt-1 z-[999] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-1"
          >
            <button
              onClick={() => { setMenuOpen(false); if (canEdit) onPitcherSubstitute?.(); }}
              disabled={!canEdit}
              title={canEdit ? "Substitute pitcher" : "Only the fielding team can sub the pitcher"}
              className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 ${canEdit ? "hover:bg-slate-800 text-slate-200" : "text-slate-600 cursor-not-allowed"}`}
            >
              <RefreshCw size={12} /> Substitute
            </button>
            <button
              onClick={() => { setMenuOpen(false); onPitcherInfo?.(pitcher); }}
              className="w-full px-3 py-2 text-left text-xs hover:bg-slate-800 flex items-center gap-2 text-slate-200"
            >
              <Zap size={12} /> Player Card
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Hoisted to module scope so React keeps the same component identity across BattingLineup
// re-renders. Defining it inside the parent caused every roster row to unmount + remount on
// every parent render, which killed the hover state and made clicks land on torn-down nodes.
function PlayerRow({ batter, idx, isBench, isCurrent, isOnDeck, isOpen, onToggle, onSub, onDetails, canEdit, tc }) {
  const isSubbed = batter.subbed_out;
  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        disabled={isSubbed}
        className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all text-left ${
          isCurrent
            ? 'bg-amber-500/20 border border-amber-500/40'
            : isOnDeck
              ? 'bg-slate-800/50 border border-slate-700/50'
              : 'border border-transparent hover:bg-slate-800/30'
        } ${isSubbed ? 'opacity-40 cursor-not-allowed' : ''}`}
      >
        <span className={`text-xs font-mono w-5 font-bold ${isCurrent ? 'text-amber-400' : isBench ? 'text-blue-400' : 'text-slate-600'}`}>
          {isBench ? '•' : idx + 1}
        </span>
        <span className={`text-[10px] font-mono w-7 px-1 py-0.5 rounded text-center font-bold ${
          isCurrent ? 'bg-amber-500 text-black'
          : isBench ? 'bg-blue-500/20 text-blue-400'
          : `${tc.bg} ${tc.text}`
        }`}>
          {batter.position}
        </span>
        <span className={`flex-1 text-xs truncate ${isCurrent ? 'font-bold text-white' : 'text-slate-300'} ${isSubbed ? 'line-through' : ''}`}>
          {batter.name}
        </span>
        {trimRange(batter) && (
          <span className="text-[9px] text-slate-600 font-mono flex-shrink-0">{trimRange(batter)}</span>
        )}
        {isCurrent && <span className="text-amber-400 text-xs">▶</span>}
      </button>

      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full mt-1 z-[999] bg-slate-900 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[140px]"
        >
          <button
            onClick={() => onSub(batter, idx)}
            disabled={!canEdit}
            title={canEdit ? "Substitute batter" : "Only the batting team can sub batters"}
            className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 ${canEdit ? "hover:bg-slate-800 text-slate-200" : "text-slate-600 cursor-not-allowed"}`}
          >
            <RefreshCw size={12} /> Substitute
          </button>
          <button
            onClick={() => onDetails(batter)}
            className="w-full px-3 py-2 text-left text-xs hover:bg-slate-800 flex items-center gap-2 text-slate-200"
          >
            <Zap size={12} /> Player Card
          </button>
        </div>
      )}
    </div>
  );
}

// Batting Lineup Display
function BattingLineup({ game, onSubPlayer, onPlayerDetails, canEdit = true }) {
  const battingTeam = game.state.half === "top" ? game.away_team : game.home_team;
  const activeLineup = getLineup(battingTeam);
  const benchFieldPlayers = getBench(battingTeam);
  const allPitchers = battingTeam.players.filter(p => p.position === "P");
  const subbedOut = battingTeam.players.filter(p => p.subbed_out);
  // Bench: available field players, then pitchers at bottom
  const benchPlayers = [...benchFieldPlayers, ...allPitchers];
  const currentBatterIndex = getBatterIndex(game.state) % (activeLineup.length || 1);
  const [openDropdown, setOpenDropdown] = useState(null);
  const tc = teamColor(battingTeam.color);

  useEffect(() => {
    const handleClick = () => setOpenDropdown(null);
    if (openDropdown) document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [openDropdown]);

  const handleSub = (batter, idx) => {
    setOpenDropdown(null);
    if (canEdit) onSubPlayer(batter, idx);
  };
  const handleDetails = (batter) => {
    setOpenDropdown(null);
    onPlayerDetails?.(batter);
  };

  return (
    <div data-testid="batting-lineup" className="sidebar-section flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-black tracking-widest ${tc.text}`}>Batting</span>
        <span className="text-[9px] text-slate-500">{battingTeam.name}</span>
      </div>

      <div className="space-y-0.5">
        {activeLineup.map((batter, idx) => (
          <PlayerRow
            key={batter.id}
            batter={batter} idx={idx} isBench={false}
            isCurrent={idx === currentBatterIndex}
            isOnDeck={idx === (currentBatterIndex + 1) % (activeLineup.length || 1)}
            isOpen={openDropdown === batter.id}
            onToggle={() => setOpenDropdown(openDropdown === batter.id ? null : batter.id)}
            onSub={handleSub}
            onDetails={handleDetails}
            canEdit={canEdit}
            tc={tc}
          />
        ))}
      </div>

      {benchPlayers.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black tracking-widest text-blue-400">Bench</span>
            <span className="text-[9px] text-slate-500">{benchPlayers.length}</span>
          </div>
          <div className="space-y-0.5">
            {benchPlayers.map((batter, idx) => (
              <PlayerRow
                key={batter.id}
                batter={batter} idx={idx} isBench={true}
                isCurrent={false}
                isOnDeck={false}
                isOpen={openDropdown === batter.id}
                onToggle={() => setOpenDropdown(openDropdown === batter.id ? null : batter.id)}
                onSub={handleSub}
                onDetails={handleDetails}
                canEdit={canEdit}
                tc={tc}
              />
            ))}
          </div>
        </div>
      )}
      
      {subbedOut.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-700/50">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-black tracking-widest text-slate-600">Out of Game</span>
            <span className="text-[9px] text-slate-600">{subbedOut.length}</span>
          </div>
          <div className="space-y-0.5">
            {subbedOut.map((player) => (
              <div key={player.id} className="flex items-center gap-2 p-2 rounded-lg opacity-30 cursor-not-allowed">
                <span className="text-xs font-mono w-5 text-slate-700">X</span>
                <span className="text-[10px] font-mono w-7 px-1 py-0.5 rounded text-center font-bold bg-slate-800/50 text-slate-600">{player.position}</span>
                <span className="flex-1 text-xs truncate text-slate-600 line-through">{player.name}</span>
                {trimRange(player) && (
                  <span className="text-[9px] text-slate-700 font-mono flex-shrink-0">{trimRange(player)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const ROSTER_LS_WIDTH_KEY = "junkball_rosterWidth";
const ROSTER_LS_COLLAPSED_KEY = "junkball_rosterCollapsed";
const ROSTER_DEFAULT_WIDTH = 280;
const ROSTER_MIN_WIDTH = 60;

function RosterPanel({ game, onPitcherClick, onSubPlayer, onPlayerDetails, onPitcherSubstitute, canEditPitcher = true, canEditBatters = true }) {
  const [width, setWidth] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(ROSTER_LS_WIDTH_KEY) || "", 10);
      if (!isNaN(v) && v > 0) return v;
    } catch {}
    return ROSTER_DEFAULT_WIDTH;
  });
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(ROSTER_LS_COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  const [isDragging, setIsDragging] = useState(false);
  const minWidth = ROSTER_MIN_WIDTH;
  const maxWidth = Math.floor(window.innerWidth * 0.333);

  useEffect(() => {
    try { localStorage.setItem(ROSTER_LS_WIDTH_KEY, String(width)); } catch {}
  }, [width]);
  useEffect(() => {
    try { localStorage.setItem(ROSTER_LS_COLLAPSED_KEY, collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);
  
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  
  useEffect(() => {
    if (!isDragging) return;
    
    const handleMouseMove = (e) => {
      const newWidth = window.innerWidth - e.clientX;
      if (newWidth < minWidth + 20) {
        setCollapsed(true);
        setWidth(minWidth);
      } else {
        setCollapsed(false);
        setWidth(Math.min(Math.max(newWidth, 120), maxWidth));
      }
    };
    
    const handleMouseUp = () => setIsDragging(false);
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, maxWidth, minWidth]);

  if (collapsed) {
    return (
      <div 
        data-testid="roster-panel-collapsed"
        style={{ width: minWidth }} 
        className="h-full bg-slate-900/50 border-l border-slate-800 flex flex-col items-center py-3 relative"
      >
        {/* Drag Handle */}
        <div
          onMouseDown={handleMouseDown}
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors"
        />
        
        {/* Expand Button */}
        <button
          onClick={() => { setCollapsed(false); setWidth(ROSTER_DEFAULT_WIDTH); }}
          className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-amber-400 transition-colors mb-4"
        >
          <PanelRightOpen size={18} />
        </button>
        
        {/* Mini indicators could go here */}
      </div>
    );
  }

  return (
    <div 
      data-testid="roster-panel"
      style={{ width }} 
      className="h-full bg-slate-900/50 border-l border-slate-800 flex flex-col relative"
    >
      {/* Drag Handle */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors"
      />
      
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        <CurrentPitcherDisplay
          game={game}
          onPitcherSubstitute={onPitcherSubstitute}
          onPitcherInfo={onPlayerDetails}
          canEdit={canEditPitcher}
        />
        <BattingLineup
          game={game}
          onSubPlayer={onSubPlayer}
          onPlayerDetails={onPlayerDetails}
          canEdit={canEditBatters}
        />
      </div>
    </div>
  );
}

// memo: roster only depends on game + the action callbacks; re-render on every GameBoard
// render is wasted work. Caller should pass stable callback refs.
export default memo(RosterPanel);
